import { CHART_COLORS } from '@/constants/chartColors'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getRevenueTargetsWithActuals } from '@/modules/Dashboard/services'
import { formatCurrency, formatNumeral } from '@/utils/function'
import { TrendingDown, TrendingUp } from 'lucide-react'
import { useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import { getCompanyRevenue } from '../../../services'
import { PDFExportButton } from '../ExportButtons'

interface ActualTargetProps {
    filters: {
        gte?: string
        lte?: string
        employee_id?: number[]
    }
}

const COLORS = {
    renewal: CHART_COLORS.EMERALD_GREEN,
    newSale: CHART_COLORS.PRIMARY_BLUE,
    upsell: CHART_COLORS.AMBER_ORANGE,
}

const ActualTarget: React.FC<ActualTargetProps> = ({ filters }) => {
    const { t } = useTranslation()
    const chartRef = useRef<HTMLDivElement>(null)

    // Lấy target của công ty
    const { data: target, renderState: renderTargetState } =
        useQueryWithHandler({
            queryKey: [
                QUERY_KEYS.REPORTS_BUSINESS_TARGETS,
                'target-vs-actual-company',
                filters.gte,
                filters.lte,
            ],
            queryFn: () =>
                getRevenueTargetsWithActuals({
                    gte: filters.gte,
                    lte: filters.lte,
                }),
            enabled: !!filters.gte && !!filters.lte,
        })

    // Lấy actual revenue của công ty
    const { data: companyRevenueData, renderState: renderPerformanceState } =
        useQueryWithHandler({
            queryKey: [
                QUERY_KEYS.REPORTS_BUSINESS_TARGETS,
                'company-revenue',
                filters.gte,
                filters.lte,
            ],
            queryFn: () =>
                getCompanyRevenue({ gte: filters.gte, lte: filters.lte }),
            enabled: !!filters.gte && !!filters.lte,
        })

    // Calculate metrics
    const metrics = useMemo(() => {
        const targetAmount =
            target?.data?.result?.summary?.total_target_amount || 0
        const revenue = companyRevenueData?.employee_revenue || {}
        const renewalRevenue = revenue.renewal_revenue || 0
        const newSaleRevenue = revenue.new_sale_revenue || 0
        const upsellRevenue = revenue.upsell_revenue || 0
        const actualTotal = renewalRevenue + newSaleRevenue + upsellRevenue
        const achievement =
            targetAmount > 0 ? (actualTotal / targetAmount) * 100 : 0
        const gap = targetAmount - actualTotal

        return {
            target: targetAmount,
            actual: actualTotal,
            achievement,
            gap,
            renewal: renewalRevenue,
            newSale: newSaleRevenue,
            upsell: upsellRevenue,
        }
    }, [target, companyRevenueData])

    // Pie chart data
    const pieData = useMemo(
        () => [
            {
                name: t('employees.performance.renewal'),
                value: metrics.renewal,
                color: COLORS.renewal,
            },
            {
                name: t('employees.performance.new_sale'),
                value: metrics.newSale,
                color: COLORS.newSale,
            },
            {
                name: t('employees.performance.upsell'),
                value: metrics.upsell,
                color: COLORS.upsell,
            },
        ],
        [metrics, t]
    )

    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const data = payload[0]
            const percent =
                metrics.actual > 0
                    ? ((data.value / metrics.actual) * 100).toFixed(1)
                    : 0
            return (
                <div className="bg-white px-3 py-2 border border-gray-200 rounded shadow-lg">
                    <p
                        className="text-sm font-medium"
                        style={{ color: data.payload.color }}
                    >
                        {data.name}
                    </p>
                    <p className="text-sm text-gray-700">
                        {formatCurrency(data.value)} ({percent}%)
                    </p>
                </div>
            )
        }
        return null
    }

    const renderComponent = renderTargetState() || renderPerformanceState()
    if (renderComponent) return renderComponent

    const isAchieved = metrics.achievement >= 100
    const progressPercent = Math.min(metrics.achievement, 100)

    return (
        <div
            className="bg-white p-4 rounded-md shadow space-y-4"
            ref={chartRef}
        >
            {/* Header */}
            <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-800">
                    {t('employees.performance.revenue_target')}
                </h3>
                <PDFExportButton chartRef={chartRef} filename="actual-target" />
            </div>

            {/* Achievement Card */}
            <div
                className={`rounded-lg p-4 ${isAchieved ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`}
            >
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        {isAchieved ? (
                            <TrendingUp className="w-5 h-5 text-emerald-600" />
                        ) : (
                            <TrendingDown className="w-5 h-5 text-amber-600" />
                        )}
                        <span
                            className={`text-3xl font-bold ${isAchieved ? 'text-emerald-600' : 'text-amber-600'}`}
                        >
                            {formatNumeral(metrics.achievement, 1)}%
                        </span>
                    </div>
                    <div className="text-right">
                        <p className="text-xs text-gray-500 uppercase tracking-wide">
                            {isAchieved
                                ? t('common.exceeded')
                                : t('common.remaining')}
                        </p>
                        <p
                            className={`text-lg font-semibold ${isAchieved ? 'text-emerald-600' : 'text-amber-600'}`}
                        >
                            {formatCurrency(Math.abs(metrics.gap))}
                        </p>
                    </div>
                </div>

                {/* Progress Bar */}
                <div className="relative h-4 bg-gray-200 rounded-full overflow-hidden">
                    <div
                        className={`absolute h-full rounded-full transition-all duration-500 ${isAchieved ? 'bg-emerald-500' : 'bg-amber-500'}`}
                        style={{ width: `${progressPercent}%` }}
                    />
                    {/* Target marker at 100% */}
                    <div className="absolute right-0 top-0 h-full w-0.5 bg-gray-600" />
                </div>

                {/* Labels */}
                <div className="flex justify-between mt-2 text-xs">
                    <div>
                        <span className="text-gray-500">
                            {t('employees.performance.actual_achieved')}:{' '}
                        </span>
                        <span className="font-semibold text-gray-800">
                            {formatCurrency(metrics.actual)}
                        </span>
                    </div>
                    <div>
                        <span className="text-gray-500">
                            {t('employees.performance.commitment')}:{' '}
                        </span>
                        <span className="font-semibold text-gray-800">
                            {formatCurrency(metrics.target)}
                        </span>
                    </div>
                </div>
            </div>

            {/* Revenue Breakdown */}
            <div className="flex items-center gap-4">
                {/* Donut Chart */}
                <div className="w-32 h-32 flex-shrink-0">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={pieData}
                                cx="50%"
                                cy="50%"
                                innerRadius={30}
                                outerRadius={50}
                                dataKey="value"
                                strokeWidth={2}
                                stroke="#fff"
                            >
                                {pieData.map((entry, index) => (
                                    <Cell key={index} fill={entry.color} />
                                ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                {/* Legend with values */}
                <div className="flex-1 space-y-2">
                    {pieData.map((item) => {
                        const percent =
                            metrics.actual > 0
                                ? ((item.value / metrics.actual) * 100).toFixed(
                                      1
                                  )
                                : 0
                        return (
                            <div
                                key={item.name}
                                className="flex items-center justify-between"
                            >
                                <div className="flex items-center gap-2">
                                    <div
                                        className="w-3 h-3 rounded-full"
                                        style={{ backgroundColor: item.color }}
                                    />
                                    <span className="text-sm text-gray-700">
                                        {item.name}
                                    </span>
                                </div>
                                <div className="text-right">
                                    <span className="text-sm font-medium text-gray-800">
                                        {formatCurrency(item.value)}
                                    </span>
                                    <span className="text-xs text-gray-500 ml-1">
                                        ({percent}%)
                                    </span>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}

export default ActualTarget
